import { useEffect, useRef, useState, useCallback } from "react";
import { toast } from "sonner";

/**
 * Hook for connecting to the GoFood real-time WebSocket server.
 * Auto-reconnects on disconnect, plays sounds for high-priority events,
 * and shows toast notifications for incoming events.
 */
export const useWebSocket = (token, options = {}) => {
  const {
    onMessage,
    onConnect,
    onDisconnect,
    showToasts = true,
    enableSound = true,
  } = options;

  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const pingIntervalRef = useRef(null);
  const [isConnected, setIsConnected] = useState(false);
  const [lastMessage, setLastMessage] = useState(null);
  const onMessageRef = useRef(onMessage);

  useEffect(() => {
    onMessageRef.current = onMessage;
  }, [onMessage]);

  const playSound = useCallback((soundType) => {
    if (!enableSound) return;
    try {
      // Simple tone generator using Web Audio API (no external assets needed)
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);

      const sounds = {
        order: { freq: 880, duration: 0.25 },
        delivery: { freq: 1046, duration: 0.2 },
        notification: { freq: 660, duration: 0.15 },
        money: { freq: 1320, duration: 0.3 },
      };
      const cfg = sounds[soundType] || sounds.notification;

      osc.frequency.value = cfg.freq;
      osc.type = "sine";
      gain.gain.setValueAtTime(0.2, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(
        0.001,
        ctx.currentTime + cfg.duration
      );

      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + cfg.duration);
    } catch {
      // Audio not available — silent fallback
    }
  }, [enableSound]);

  const showBrowserNotification = useCallback((title, message) => {
    if (!("Notification" in window)) return;
    if (Notification.permission === "granted") {
      try {
        new Notification(title, {
          body: message,
          icon: "/favicon.ico",
          badge: "/favicon.ico",
          silent: false,
        });
      } catch {
        // ignore
      }
    }
  }, []);

  const connect = useCallback(() => {
    if (!token) return;
    if (wsRef.current?.readyState === WebSocket.OPEN) return;

    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    const wsUrl = backendUrl.replace(/^http/, "ws") + `/api/ws?token=${token}`;

    try {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        setIsConnected(true);
        if (onConnect) onConnect();

        // Ping every 30s to keep connection alive
        pingIntervalRef.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: "ping" }));
          }
        }, 30000);
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);

          if (data.type === "pong" || data.type === "connection") {
            // System frames — do NOT call setLastMessage to avoid forcing a
            // re-render of every consumer (e.g. App) every 30s on each ping.
            return;
          }

          setLastMessage(data);

          if (showToasts && data.title) {
            toast(data.title, {
              description: data.message,
              duration: 5000,
            });
          }

          if (data.sound) {
            playSound(data.sound);
          }

          if (data.title) {
            showBrowserNotification(data.title, data.message || "");
          }

          if (onMessageRef.current) {
            onMessageRef.current(data);
          }

          // Bridge: also dispatch as a window-level CustomEvent so any
          // component can listen without prop-drilling the WS hook.
          try {
            const e1 = new CustomEvent("gofood-ws", { detail: data });
            const e2 = new CustomEvent("gofood:ws", { detail: data });
            window.dispatchEvent(e1);
            window.dispatchEvent(e2);
          } catch {}
        } catch (err) {
          console.error("WS parse error:", err);
        }
      };

      ws.onclose = () => {
        setIsConnected(false);
        if (onDisconnect) onDisconnect();

        if (pingIntervalRef.current) {
          clearInterval(pingIntervalRef.current);
          pingIntervalRef.current = null;
        }

        // Reconnect after 3s
        reconnectTimeoutRef.current = setTimeout(() => {
          connect();
        }, 3000);
      };

      ws.onerror = (err) => {
        console.error("WS error:", err);
      };
    } catch (err) {
      console.error("WS connect error:", err);
    }
  }, [token, onConnect, onDisconnect, showToasts, playSound, showBrowserNotification]);

  const sendMessage = useCallback((message) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(message));
      return true;
    }
    return false;
  }, []);

  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
      pingIntervalRef.current = null;
    }
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (token) {
      connect();
    }
    return () => disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  return {
    isConnected,
    lastMessage,
    sendMessage,
    disconnect,
    reconnect: connect,
  };
};

/**
 * Request browser notification permission.
 * Call once at app startup or on a user-initiated action.
 */
export const requestNotificationPermission = async () => {
  if (!("Notification" in window)) return false;
  if (Notification.permission === "granted") return true;
  if (Notification.permission === "denied") return false;

  const permission = await Notification.requestPermission();
  return permission === "granted";
};
